import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

export interface OrganizationOut {
  created_at: string;
  id: string;
  name: string;
  settings: Record<string, unknown> | null;
  timezone: string | null;
  updated_at: string;
}

export function useOrganization() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery<OrganizationOut>({
    enabled: !!orgId,
    // OrgTimezoneProvider mounts this app-wide, so the global defaults
    // (staleTime: 10s, refetchOnWindowFocus: true) would refetch it on every
    // window focus for the app's lifetime — for a value (org name/timezone)
    // that changes about once per clinic's lifetime. Scoped to this query
    // only; other queries keep the global defaults.
    staleTime: Infinity,
    queryKey: ['organization', orgId],
    queryFn: async () => {
      const { data } = await clientApi.get<OrganizationOut>(`/api/v1/organizations/${orgId}`);
      return data;
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
    },
    mutationFn: async (body: { name?: string; hidden_modules?: string[]; settings?: Record<string, unknown> | null; timezone?: string }) => {
      const { data } = await clientApi.patch<OrganizationOut>(`/api/v1/organizations/${orgId}`, body);
      return data;
    },
  });
}
